/**
 * Distance to Target — how far we are from where we want to be (N).
 *
 * For every tracked component (service-catalog stack item, AI model, OS,
 * dependency) we define a desired-state target N and measure the GAP: how many
 * major versions behind, how many model generations behind, or how close to
 * EOL. Generalises what the AI model registry already does
 * ("deepseek-chat → deepseek-v4-pro = 1 generation behind") across the estate.
 *
 * Ranked by urgency (how far behind × criticality × EOL risk × losing an
 * exploitation race). Each row shows current → target with a gap badge, an
 * urgency bar, and a progress-to-target bar. Admins can click a target to set
 * a different N (the policy endpoint).
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Target, RefreshCw, AlertTriangle, Gauge, CheckCircle2, Zap, Pencil,
  Database, Cpu, Box, Server, Package, ChevronRight,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'
import RelatedItems from '../components/RelatedItems'
import KpiTile from '../components/KpiTile'
import PageHeader from '../components/PageHeader'
import ErrorBanner from '../components/ErrorBanner'
import EmptyState from '../components/EmptyState'

interface Distance {
  component: string
  category: string
  current: string
  target: string
  gap_magnitude: number
  gap_label: string
  distance_kind: string
  on_target: boolean
  urgency: number
  source: string
  raw?: {
    normalised?: string
    tiers?: number[]
    services?: string[]
    eol_days?: number | null
    eol_state?: string | null
    losing_exploit_race?: boolean
    provider?: string
  }
}
interface Summary {
  total_tracked: number
  on_target_count: number
  behind_count: number
  worst_offenders: Distance[]
  by_category: Record<string, { total: number; behind: number; on_target: number; avg_urgency: number }>
  worst_category: string | null
  overall_drift_score: number
  generated_at: string
}

const CAT_ICON: Record<string, React.ElementType> = {
  db: Database, runtime: Cpu, software: Box, ai_model: Cpu, os: Server, dependency: Package,
}
const CAT_LABEL: Record<string, string> = {
  db: 'Database', runtime: 'Runtime', software: 'Software', ai_model: 'AI Model',
  os: 'OS', dependency: 'Dependency',
}

function gapTone(d: Distance): string {
  if (d.on_target) return 'bg-emerald/10 text-emerald border-emerald/30'
  if (d.distance_kind === 'eol_days') return 'bg-rose/10 text-rose-light border-rose/30'
  if (d.gap_magnitude >= 2) return 'bg-amber/10 text-amber-light border-amber/30'
  return 'bg-cyan/10 text-cyan border-cyan/30'
}

function urgencyTone(u: number): string {
  if (u >= 60) return 'bg-rose-light'
  if (u >= 30) return 'bg-amber-light'
  if (u > 0) return 'bg-cyan'
  return 'bg-emerald-light'
}

/** Progress-to-target bar: how close to N (100% = on target). */
function ProgressToTarget({ d }: { d: Distance }) {
  // For version/generation gaps, model closeness as 1/(1+gap). EOL rows are
  // "behind" regardless, so show a low bar weighted by urgency.
  let pct: number
  if (d.on_target) pct = 100
  else if (d.distance_kind === 'eol_days') pct = Math.max(5, 100 - d.urgency)
  else pct = Math.round((1 / (1 + Math.max(0, d.gap_magnitude))) * 100)
  return (
    <div className="h-1.5 rounded-full bg-bg-elev overflow-hidden" title={`${pct}% to target`}>
      <div className={clsx('h-full rounded-full', d.on_target ? 'bg-emerald-light' : 'bg-cyan')}
           style={{ width: `${pct}%` }} />
    </div>
  )
}

export default function TargetStatePage() {
  const [summary, setSummary] = useState<Summary | null>(null)
  const [distances, setDistances] = useState<Distance[]>([])
  const [loading, setLoading] = useState(true)
  const [recomputing, setRecomputing] = useState(false)
  const [error, setError] = useState('')
  const [msg, setMsg] = useState('')
  const [catFilter, setCatFilter] = useState<string>('all')
  const [behindOnly, setBehindOnly] = useState(false)
  const [editing, setEditing] = useState<string | null>(null)
  const [editVal, setEditVal] = useState('')

  const load = useCallback(async () => {
    setLoading(true); setError('')
    try {
      const [s, d] = await Promise.all([
        api.get('/api/target-state/summary'),
        api.get('/api/target-state/distances'),
      ])
      setSummary(s.data as Summary)
      setDistances((d.data.distances || []) as Distance[])
    } catch (e: unknown) {
      setError((e as { message?: string })?.message || 'failed to load')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  const recompute = async () => {
    setRecomputing(true); setMsg(''); setError('')
    try {
      const r = await api.post('/api/target-state/recompute')
      const dd = r.data as { upserted: number; behind: number }
      setMsg(`Recomputed — ${dd.upserted} tracked, ${dd.behind} behind.`)
      await load()
    } catch (e: unknown) {
      const status = (e as { response?: { status?: number } })?.response?.status
      setError(status === 401 ? 'Admin role required to recompute.' : 'Recompute failed.')
    } finally {
      setRecomputing(false)
    }
  }

  const saveTarget = async (d: Distance) => {
    const key = d.raw?.normalised || d.component.toLowerCase()
    setMsg(''); setError('')
    try {
      await api.post('/api/target-state/policy', { [key]: editVal.trim() })
      setMsg(`Target for ${d.component} set to ${editVal.trim()}. Recomputing…`)
      setEditing(null)
      await recompute()
    } catch (e: unknown) {
      const status = (e as { response?: { status?: number } })?.response?.status
      setError(status === 401 ? 'Admin role required to edit targets.' : 'Failed to set target.')
      setEditing(null)
    }
  }

  const categories = useMemo(() => {
    const set = new Set(distances.map(d => d.category))
    return ['all', ...Array.from(set)]
  }, [distances])

  const visible = useMemo(() => distances.filter(d =>
    (catFilter === 'all' || d.category === catFilter) &&
    (!behindOnly || !d.on_target),
  ), [distances, catFilter, behindOnly])

  const empty = distances.length === 0
  const drift = summary?.overall_drift_score ?? 0

  return (
    <div className="space-y-5 animate-fade-in">
      <PageHeader
        icon={Target} iconClassName="text-cyan" title="DISTANCE TO TARGET"
        subtitle="how far we are from where we want to be (N)"
        actions={<>
          {msg && <span className="text-[10px] text-emerald">{msg}</span>}
          <button type="button" onClick={() => void recompute()} disabled={recomputing} className="btn-ghost">
            {recomputing ? <RefreshCw size={11} className="animate-spin" /> : <Zap size={11} />}
            {recomputing ? 'Computing…' : 'Recompute'}
          </button>
          <button type="button" onClick={() => void load()} disabled={loading} className="btn-ghost">
            <RefreshCw size={11} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </>}
      />

      <div className="card p-3 text-[12px] text-text-secondary leading-relaxed">
        <span className="text-text-primary font-medium">Distance to N.</span>{' '}
        For every tracked component we define a desired-state target — latest stable major / LTS
        for software & runtimes, the newest available model for AI, current-supported release for
        OS — and measure the gap: how many <em>major versions</em> behind, how many
        <em> generations</em> behind, or how close to <em>EOL</em>. Ranked by urgency: how far
        behind × criticality × EOL risk × whether it's also losing an exploitation race.
        <div className="mt-1.5 text-[10px] text-text-muted">
          Sources: service-catalog declared stack + EOL calendar + AI model registry. OS gaps
          appear once CDM inventory (SCCM / BMC Helix) lands.
        </div>
      </div>

      <ErrorBanner message={error} />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiTile label="On target" value={summary?.on_target_count ?? 0}
              tone="text-emerald-light" hint="at desired state N" />
        <KpiTile label="Behind" value={summary?.behind_count ?? 0}
              tone={(summary?.behind_count ?? 0) ? 'text-amber-light' : 'text-text-primary'}
              hint={`of ${summary?.total_tracked ?? 0} tracked`} />
        <KpiTile label="Drift score" value={drift}
              tone={drift >= 40 ? 'text-rose-light' : drift >= 15 ? 'text-amber-light' : 'text-emerald-light'}
              hint="0 = all on target, 100 = max drift" />
        <KpiTile label="Worst category"
              value={summary?.worst_category ? (CAT_LABEL[summary.worst_category] || summary.worst_category) : '—'}
              hint="highest avg urgency" />
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        {categories.map(c => (
          <button key={c} type="button" onClick={() => setCatFilter(c)}
                  className={clsx('text-[11px] px-2 py-0.5 rounded border',
                    catFilter === c ? 'bg-cyan/10 text-cyan border-cyan/30'
                      : 'border-border text-text-muted hover:text-text-primary')}>
            {c === 'all' ? 'All' : (CAT_LABEL[c] || c)}
          </button>
        ))}
        <label className="ml-auto flex items-center gap-1.5 text-[11px] text-text-muted cursor-pointer">
          <input type="checkbox" checked={behindOnly} onChange={e => setBehindOnly(e.target.checked)} />
          Behind only
        </label>
      </div>

      {/* The list */}
      {empty ? (
        <EmptyState icon={Gauge}
          title="No tracked components yet"
          hint={<>Declare a service stack in the <Link to="/app/services" className="text-cyan">Service Catalog</Link>{' '}
            or configure an AI provider key, then Recompute.</>} />
      ) : (
        <div className="space-y-2">
          {visible.map(d => {
            const Icon = CAT_ICON[d.category] || Box
            const losing = d.raw?.losing_exploit_race
            return (
              <div key={`${d.component}-${d.category}`} className="card p-3">
                <div className="flex items-center gap-3 flex-wrap">
                  <Icon size={15} className="text-text-muted shrink-0" />
                  <div className="min-w-0">
                    <div className="text-[13px] font-semibold text-text-primary flex items-center gap-1.5">
                      {d.component}
                      <span className="text-[9px] uppercase tracking-wider text-text-muted">
                        {CAT_LABEL[d.category] || d.category}
                      </span>
                      {d.on_target && <CheckCircle2 size={12} className="text-emerald-light" />}
                    </div>
                    <div className="text-[11px] text-text-muted flex items-center gap-1 mt-0.5">
                      <span className="text-text-secondary font-medium">{d.current}</span>
                      <ChevronRight size={11} />
                      {editing === `${d.component}-${d.category}` ? (
                        <span className="inline-flex items-center gap-1">
                          <input autoFocus value={editVal} onChange={e => setEditVal(e.target.value)}
                                 onKeyDown={e => { if (e.key === 'Enter') void saveTarget(d); if (e.key === 'Escape') setEditing(null) }}
                                 className="w-16 bg-bg-elev border border-cyan/40 rounded px-1 text-[11px] text-text-primary" />
                          <button type="button" onClick={() => void saveTarget(d)} className="text-cyan text-[10px]">save</button>
                          <button type="button" onClick={() => setEditing(null)} className="text-text-muted text-[10px]">cancel</button>
                        </span>
                      ) : (
                        <button type="button"
                                onClick={() => { setEditing(`${d.component}-${d.category}`); setEditVal(d.target) }}
                                className="text-text-secondary font-medium inline-flex items-center gap-0.5 hover:text-cyan"
                                title="Set a different target N (admin)">
                          {d.target} <Pencil size={9} className="opacity-50" />
                        </button>
                      )}
                    </div>
                  </div>

                  <span className={clsx('text-[10px] px-1.5 py-0.5 rounded border ml-1', gapTone(d))}>
                    {d.gap_label}
                  </span>

                  {losing && (
                    <Link to="/app/exploitation-race"
                          className="text-[10px] px-1.5 py-0.5 rounded border bg-rose/10 text-rose-light border-rose/30 inline-flex items-center gap-1"
                          title="This component is also losing an exploitation race — fix first">
                      <AlertTriangle size={10} /> also losing exploit race
                    </Link>
                  )}

                  <div className="ml-auto flex items-center gap-2 w-40 shrink-0">
                    <div className="flex-1 h-1.5 rounded-full bg-bg-elev overflow-hidden" title={`urgency ${Math.round(d.urgency)}`}>
                      <div className={clsx('h-full rounded-full', urgencyTone(d.urgency))}
                           style={{ width: `${Math.max(2, Math.min(100, d.urgency))}%` }} />
                    </div>
                    <span className="text-[10px] text-text-muted w-7 text-right tabular-nums">{Math.round(d.urgency)}</span>
                  </div>
                </div>

                {/* Progress to target N */}
                <div className="mt-2">
                  <ProgressToTarget d={d} />
                </div>

                {/* Cross-feature provenance — only for behind-target gaps, to
                    keep on-target rows quiet. */}
                {!d.on_target && (
                  <RelatedItems entityType="target_gap" entityKey={d.component} className="mt-2" />
                )}
              </div>
            )
          })}
          {visible.length === 0 && (
            <div className="card p-4 text-center text-[12px] text-text-muted">
              Nothing matches this filter.
            </div>
          )}
        </div>
      )}
    </div>
  )
}
